import { useRef, useState, type CSSProperties } from "react"

export type UseOverflowPopoverOptions = {
  /**
   * Open on hover even when nothing is cut off — for content that has more
   * to show than the cell does (a breakdown's full legend).
   */
  isAlwaysShown?: boolean
}

/**
 * Hover-to-reveal for truncated cell content, shared by body and summary
 * cells: while the pointer rests on an element whose content overflows it,
 * `popoverStyle` anchors a `.ui-table-cell-popover` above the element's
 * top-left corner for the caller to render the full content in.
 */
export function useOverflowPopover<T extends HTMLElement>(
  options: UseOverflowPopoverOptions = {},
) {
  const { isAlwaysShown = false } = options
  const ref = useRef<T>(null)
  const [isShown, setIsShown] = useState(false)

  function onMouseEnter() {
    const el = ref.current
    if (el && (isAlwaysShown || el.scrollWidth > el.clientWidth)) {
      setIsShown(true)
    }
  }

  function onMouseLeave() {
    setIsShown(false)
  }

  let popoverStyle: CSSProperties | undefined
  if (isShown && ref.current) {
    const rect = ref.current.getBoundingClientRect()
    popoverStyle = {
      position: "fixed",
      left: rect.left,
      bottom: window.innerHeight - rect.top,
    }
  }

  return { ref, popoverStyle, onMouseEnter, onMouseLeave }
}
